/* iter150AI.2 — Dealer Quote Tutorial Wizard.
 *
 * A small in-app guided walkthrough that explains the dealer-quote
 * workflow. Six steps, each backed by a markdown article fetched from
 * the multi-language help section endpoint.
 *
 * Triggered from the DealerWorkflowBar "Quick tour" button on
 * QuoteDetailPage; can also be launched standalone from the admin Help
 * menu (route mounted in App.js).
 *
 * Language follows the user's i18n preference (useT().lang).
 */
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  CaretLeft, CaretRight, X, BookOpen, CheckCircle,
} from "@phosphor-icons/react";
import { useT } from "@/lib/i18n";
import { fetchHelpSection } from "@/lib/api";


export default function DealerQuoteWizard({ open, onClose, initialStep = 0 }) {
  const { lang } = useT();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(initialStep);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchHelpSection("dealer-quote", lang || "en")
      .then((r) => setArticles(r.items || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [open, lang]);

  useEffect(() => { if (open) setStep(initialStep); }, [open, initialStep]);

  if (!open) return null;

  const total = articles.length;
  const current = articles[step] || null;
  const isLast = step >= total - 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
         onClick={onClose}
         data-testid="dealer-quote-wizard">
      <div className="bg-white rounded-sm shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <BookOpen size={16} weight="bold" className="text-blue-700" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Dealer Quote Tutorial — Step {step + 1} of {total || "…"}
            </div>
            <div className="text-sm font-bold font-['Chivo'] text-slate-800"
                 data-testid="dealer-quote-wizard-title">
              {current?.title || (loading ? "Loading…" : "—")}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
                  data-testid="dealer-quote-wizard-close"
                  className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-slate-700 hover:bg-slate-100">
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto prose prose-sm max-w-none flex-1"
             data-testid="dealer-quote-wizard-body">
          {loading && <div className="text-slate-500 text-sm">Loading articles…</div>}
          {!loading && !current && (
            <div className="text-slate-500 text-sm">No tutorial content found for this language yet.</div>
          )}
          {current && <ReactMarkdown>{current.body}</ReactMarkdown>}
        </div>

        {/* Footer / nav */}
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between gap-2 bg-slate-50">
          <div className="flex items-center gap-1.5">
            {articles.map((_, i) => (
              <button key={i} onClick={() => setStep(i)}
                      data-testid={`dealer-quote-wizard-dot-${i}`}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i === step
                          ? "bg-blue-600"
                          : i < step
                            ? "bg-blue-300"
                            : "bg-slate-300 hover:bg-slate-400"}`}
                      aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    data-testid="dealer-quote-wizard-prev"
                    className="text-[10px] uppercase font-bold px-3 py-1.5 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1 disabled:opacity-40">
              <CaretLeft size={10} weight="bold" />
              Back
            </button>
            {isLast ? (
              <button onClick={onClose}
                      data-testid="dealer-quote-wizard-done"
                      className="text-[10px] uppercase font-bold px-3 py-1.5 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white inline-flex items-center gap-1">
                <CheckCircle size={10} weight="bold" />
                Finish
              </button>
            ) : (
              <button onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
                      disabled={loading || !current}
                      data-testid="dealer-quote-wizard-next"
                      className="text-[10px] uppercase font-bold px-3 py-1.5 rounded-sm bg-blue-700 hover:bg-blue-800 text-white inline-flex items-center gap-1 disabled:opacity-40">
                Next
                <CaretRight size={10} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
